import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, FileText, Trash2, Download, Loader2, CheckCircle2, FolderOpen,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface UserDoc {
  id: string;
  category: string;
  label: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
}

interface DocsResponse {
  documents: UserDoc[];
  count: number;
  categories_covered: string[];
  available_categories: Record<string, string>;
}

const fmtSize = (b: number) => {
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
};

export const DocumentsManager = ({ onChange }: { onChange?: (covered: string[]) => void }) => {
  const [docs, setDocs] = useState<UserDoc[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [covered, setCovered] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("kbis");
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents`, {
        credentials: "include" as RequestCredentials,
      });
      if (!res.ok) return;
      const data: DocsResponse = await res.json();
      setDocs(data.documents);
      setCategories(data.available_categories);
      setCovered(data.categories_covered);
      onChange?.(data.categories_covered);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
     
  }, []);

  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 MB).");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", selectedCategory);
      fd.append("label", categories[selectedCategory] || file.name);
      const res = await fetch(`${API_URL}/api/documents/upload`, {
        method: "POST",
        credentials: "include" as RequestCredentials,
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Échec de l'upload");
        return;
      }
      toast.success(`${categories[selectedCategory] || "Document"} ajouté`);
      await load();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const del = async (id: string) => {
    setDeletingId(id);
    try {
      const res = await fetch(`${API_URL}/api/documents/${id}`, {
        method: "DELETE",
        credentials: "include" as RequestCredentials,
      });
      if (res.ok) {
        toast.success("Document supprimé");
        await load();
      }
    } finally {
      setDeletingId(null);
    }
  };

  const download = async (d: UserDoc) => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${d.id}/download`, {
        credentials: "include" as RequestCredentials,
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || d.label;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card className="border-primary/20" data-testid="documents-manager">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="text-base">Mes documents d'entreprise</CardTitle>
              <CardDescription className="text-xs">
                Upload : KBIS, statuts, RIB, bilan, attestation URSSAF, CV, pièce d'identité, BP, etc. (PDF/PNG/JPG/DOCX, max 10 MB)
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {docs.length} document{docs.length !== 1 ? "s" : ""} · {covered.length} catégorie{covered.length !== 1 ? "s" : ""}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Upload row */}
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs text-muted-foreground">Catégorie</label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-9" data-testid="doc-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categories).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.docx,.doc"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            data-testid="doc-file-input"
          />
          <Button
            variant="hero"
            size="sm"
            className="gap-2 h-9"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="doc-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Upload..." : "Ajouter un document"}
          </Button>
        </div>

        {/* Docs list */}
        {docs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border/50 p-6 text-center">
            <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Aucun document pour le moment. Ajoute ton KBIS pour démarrer.
            </p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="doc-list">
            {docs.map((d) => (
              <div
                key={d.id}
                className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border/50 bg-card/40 hover:bg-card/70 transition-colors"
                data-testid={`doc-row-${d.id}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{d.label}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {d.original_filename} · {fmtSize(d.size_bytes)} · {new Date(d.uploaded_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Badge variant="secondary" className="text-[10px]">
                    {categories[d.category] || d.category}
                  </Badge>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => download(d)} data-testid={`doc-download-${d.id}`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => del(d.id)}
                    disabled={deletingId === d.id}
                    data-testid={`doc-delete-${d.id}`}
                  >
                    {deletingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentsManager;
